import type {
  Benefit,
  Bonus,
  Faq,
  Module,
  Purchase,
  Testimonial,
} from '../types/sales'

export const product = {
  name: 'YIN YOGA READY™',
  tagline: 'THE COMPLETE READY-TO-TEACH SYSTEM',
  strip: 'Classes • Sequences • Poses • Modifications • Teaching Scripts • Planning Tools',
  stripShort: 'Classes • Sequences • Poses • Modifications • Cueing • Planning',
  image:
    'https://cdn.magicpatterns.com/uploads/2bq3eTsc2FySbjBsjYf6sU/Diseno-sin-titulo-5-2.webp',
  checkoutUrl: 'https://yin-yoga-ready.impultienda.ar/checkout',
  price: '$27',
  fullPrice: '$97',
  guarantee: '14-Day Money-Back Guarantee — Zero Risk',
}

export const whatIf = {
  heading: 'WHAT IF YOUR NEXT YIN YOGA CLASS WAS ALREADY READY?',
  nots: [
    "You don't need another folder filled with random sequences.",
    "You don't need to search online every time you need a new class.",
    "You don't need to rebuild your entire session plan every week.",
  ],
  need: 'You need a system.',
  items: [
    'Ready-to-teach classes.',
    'Pose variations.',
    'Targeted sequences.',
    'Transitions.',
    'Modifications.',
    'Teaching cues.',
    'Planning tools.',
  ],
  close:
    'YIN YOGA READY™ gives you one complete professional system to choose, adapt, organize and teach Yin Yoga classes without constantly starting from a blank page.',
}

export const painSection = {
  heading: 'STOP BUILDING EVERY YIN YOGA CLASS FROM SCRATCH',
  intro: [
    'Maybe you already know how to teach Yin Yoga.',
    "That's not the problem.",
    'The problem starts when you have to keep creating the classes.',
  ],
  outro: [
    'And then, after you finish teaching...',
    'You have to plan the next one.',
    "You don't need more scattered inspiration.",
    'You need ready-to-teach structure.',
  ],
}

export const painQuestions: string[] = [
  'What sequence should I teach tonight?',
  'Which poses work well together?',
  'How long should I stay in each position?',
  "What can I substitute if a pose doesn't work for someone?",
  'Which props should I prepare?',
  'How do I adapt this sequence for a mixed-level group?',
  'What should I say during longer holds?',
  'How do I turn this 60-minute plan into a 45-minute class?',
  "What can I teach that doesn't feel exactly like last week's session?",
]

export const knowingSection = {
  heading:
    "KNOWING YIN YOGA ISN'T THE SAME AS HAVING A COMPLETE TEACHING SYSTEM",
  intro: 'Anyone can search online for “Yin Yoga sequences.”',
  scattered: [
    'Pose lists.',
    'Videos.',
    'Pinterest boards.',
    'Short routines.',
    'Blog posts.',
    'Social media ideas.',
    'Random combinations.',
  ],
  bridge:
    "But then you're still responsible for turning all of that into a class you can actually teach.",
  systemLead: 'A professional teaching system gives you:',
  system: [
    'clear class structures',
    'pose selections',
    'timing guidance',
    'props',
    'transitions',
    'modifications',
    'alternatives',
    'teaching cues',
    'complete sequences',
    'planning frameworks',
    'ready-to-use instructor resources',
  ],
  close: [
    "Because your next class shouldn't depend on whatever sequence you happened to find ten minutes before your students arrive.",
    'You should be able to open your system, choose what fits, adapt it if necessary and teach.',
  ],
}

export const turnSection = {
  heading: 'TURN HOURS OF PLANNING INTO A READY-TO-TEACH SYSTEM',
  intro:
    'Imagine opening one organized resource and having options for different teaching situations.',
  pairs: [
    { need: 'Need a complete 60-minute class?', answer: "It's there." },
    { need: 'Need a hip-focused sequence?', answer: "It's there." },
    { need: 'Need a shorter 30-minute practice?', answer: "It's there." },
    { need: 'Need an alternative pose?', answer: "It's there." },
    {
      need: 'Need a modification using a bolster or blocks?',
      answer: "It's there.",
    },
    { need: 'Need language for a longer hold?', answer: "It's there." },
    {
      need: 'Need to build a new class quickly?',
      answer: 'The framework is already there.',
    },
  ],
  close: [
    'Not another collection of disconnected Yoga ideas.',
    'One practical professional system built around the classes you actually need to teach.',
    "That's YIN YOGA READY™.",
  ],
}

export const systemIntro = {
  eyebrow: "THE COMPLETE SYSTEM YOU'VE BEEN LOOKING FOR",
  heading: 'ONE COMPLETE 6-IN-1 YIN YOGA TEACHING SYSTEM',
  nots: [
    'Not another generic Yoga guide.',
    'Not another random pose library.',
    'Not another collection of disconnected sequences.',
    'Not endless theory.',
  ],
  bridge: 'A practical professional system designed to take you from:',
  from: '“What am I teaching next?”',
  to: '“My next class is already structured.”',
  checks: [
    'Choose from ready-to-teach class formats',
    'Access organized pose and variation resources',
    'Find targeted sequences for different teaching focuses',
    'Adapt classes using practical modifications',
    'Use prepared transitions and teaching language',
    'Build new sessions without starting from zero',
    'Reduce constant searching and preparation',
    'Keep your teaching resources organized in one system',
  ],
}

export const modules: Module[] = [
  {
    n: 1,
    title: 'YIN YOGA POSE & VARIATION LIBRARY™',
    hook:
      'Stop searching through multiple resources every time you need the right pose or an alternative.',
    lead:
      'Access an organized professional library of Yin Yoga poses with practical teaching information including:',
    bullets: [
      'pose setup',
      'entry guidance',
      'exit guidance',
      'suggested hold ranges',
      'prop options',
      'common variations',
      'more accessible alternatives',
      'transition possibilities',
      'counterpose options',
      'practical instructor notes',
    ],
    notes: [
      'Find options organized by body area and teaching focus so you can locate what you need faster.',
      'Need another way to approach a pose? Need a prop-supported variation? Need something that fits better within your sequence? The options are already organized for you.',
    ],
    close: 'Find the pose. Check the options. Teach with more structure.',
  },
  {
    n: 2,
    title: 'READY-TO-TEACH YIN YOGA CLASSES™',
    hook: 'This is where planning gets dramatically simpler.',
    lead:
      "Access complete Yin Yoga classes already organized from beginning to end. You'll have class structures across different durations, including:",
    bullets: [
      '30-minute practices',
      '45-minute classes',
      '60-minute classes',
      '75-minute classes',
      'longer 90-minute sessions',
    ],
    secondLead: 'Each class is structured with:',
    secondBullets: [
      'pose order',
      'timing guidance',
      'transitions',
      'prop suggestions',
      'modification options',
      'opening structure',
      'main practice',
      'counterposes',
      'closing sequence',
      'instructor notes',
    ],
    notes: [
      'Instead of staring at a blank page and building another entire session... Choose the class. Review the structure. Make any adjustments you need. Teach.',
    ],
    close: "Your next Yin Yoga session doesn't have to begin from zero.",
  },
  {
    n: 3,
    title: 'TARGETED YIN YOGA SEQUENCE VAULT™',
    hook: 'Need a specific focus for your next class? Open the vault.',
    lead:
      "Access professionally organized Yin Yoga sequences covering different teaching directions and class situations. You'll find sequences focused around areas and formats such as:",
    bullets: [
      'hips',
      'spine',
      'legs',
      'upper body',
      'full-body practice',
      'mobility-focused sessions',
      'gentler practices',
      'beginner-friendly structures',
      'experienced practitioners',
      'morning sessions',
      'evening sessions',
      'short practices',
      'extended practices',
      'quiet-focused sessions',
      'full-body combinations',
    ],
    notes: [
      'Each sequence provides a practical structure you can use directly or integrate into a longer class.',
      "You'll also have options for changing duration, substituting poses and adapting the structure when necessary.",
    ],
    close: 'Choose the focus. Open the sequence. Start planning faster.',
  },
  {
    n: 4,
    title: 'YIN YOGA CLASS BUILDER SYSTEM™',
    hook:
      'Want to create your own classes without rebuilding everything from scratch? This module gives you the framework.',
    lead:
      'Instead of beginning with an empty page, use reusable class structures, sequencing blocks and planning formulas. Access:',
    bullets: [
      '30-to-90-minute class templates',
      'opening blocks',
      'lower-body blocks',
      'upper-body blocks',
      'full-body blocks',
      'transition structures',
      'closing blocks',
      'sequencing formulas',
      'pose combinations',
      'duration matrices',
      'teaching-focus matrices',
      'pose substitution tools',
      'reusable class planners',
      'pre-class checklists',
      'themed-class frameworks',
    ],
    notes: [
      'Mix and match previously organized structures to build new sessions faster.',
      'Need 60 minutes? Use the appropriate framework. Need to reduce it to 45? Adjust the relevant blocks. Need a gentler version? Use the substitution and adaptation options.',
    ],
    close: 'Build differently without starting over.',
  },
  {
    n: 5,
    title: 'CUEING, TRANSITIONS & TEACHING SCRIPTS™',
    hook:
      "Knowing what poses you're teaching is only part of the class. You also need to guide the experience.",
    lead:
      'Access ready-to-use professional teaching language for different moments of a Yin Yoga session, including:',
    bullets: [
      'class openings',
      'pose entry cues',
      'longer holds',
      'prop guidance',
      'intensity adjustments',
      'modifications',
      'transitions',
      'pose exits',
      'counterposes',
      'quiet moments',
      'breathing guidance',
      'relaxation',
      'class closings',
    ],
    notes: [
      'Use complete scripts when you want more support or shorter cueing references when you only need a prompt.',
      'No more thinking: “What should I say here?” “How do I transition out of this?” “What can I say without overtalking?”',
    ],
    close: 'The teaching language is already there when you need it.',
  },
  {
    n: 6,
    title: 'YIN YOGA TEACHING TOOLKIT™',
    hook:
      'This is where the entire system becomes easier to use week after week.',
    lead:
      "Access practical instructor tools designed to help you organize, adapt and reuse your Yin Yoga material. You'll find:",
    bullets: [
      'class-planning forms',
      'pre-class checklists',
      'space-preparation checklists',
      'prop reference guides',
      'class rotation tools',
      'weekly planning structures',
      'monthly planning structures',
      'class-theme organizers',
      'modification matrices',
      'pose substitution sheets',
      'post-class notes',
      'favorite-class trackers',
      'pose indexes',
      'sequence indexes',
      'class indexes',
      'reusable teaching reference tools',
    ],
    notes: [
      'Instead of keeping ideas across notebooks, saved posts, folders and random documents...',
    ],
    close: 'Keep your teaching process organized inside one practical system.',
  },
]

export const bonuses: Bonus[] = [
  {
    n: 1,
    title: 'YIN YOGA CLASS THEME VAULT™',
    hook:
      'Sometimes you already know the poses. What you need is the direction for the class.',
    lead:
      'Access a professional collection of class themes and teaching focuses designed to help you turn a sequence into a more intentional session. Use organized themes with:',
    bullets: [
      'class focus',
      'recommended structure',
      'pose-selection direction',
      'teaching tone',
      'timing considerations',
      'adaptation ideas',
      'practical ways to integrate the theme',
    ],
    notes: [
      'Instead of inventing a new concept every week... Choose a theme and build from a prepared direction.',
    ],
    close: 'More variety without rebuilding your entire teaching process.',
  },
  {
    n: 2,
    title: 'PROPS & MODIFICATIONS QUICK GUIDE™',
    hook:
      'One group. Different bodies. Different experience levels. Different needs.',
    lead:
      'This practical quick-reference resource helps you adapt common Yin Yoga positions using:',
    bullets: [
      'bolsters',
      'blocks',
      'blankets',
      'straps',
      'position changes',
      'range adjustments',
      'pose alternatives',
      'practical support options',
    ],
    notes: [
      'See how a pose can be adjusted without turning every modification into a completely different class.',
      "You'll also have quick decision tools for situations where the planned version isn't the best fit.",
    ],
    close: 'Adapt the pose without losing the structure of your class.',
  },
  {
    n: 3,
    title: 'YIN YOGA CUEING & LANGUAGE VAULT™',
    hook:
      'What do you say during a three-, four- or five-minute hold? How much should you speak? How do you guide students without filling every quiet moment?',
    lead:
      'This bonus gives you an additional organized library of professional language you can draw from when teaching. Access:',
    bullets: [
      'opening language',
      'pose-entry cues',
      'settling cues',
      'breathing prompts',
      'awareness prompts',
      'prop instructions',
      'transition language',
      'quiet-moment guidance',
      'pose-exit cues',
      'closing language',
      'adaptable teaching phrases',
    ],
    notes: [
      'Use the complete wording or select individual cues depending on your teaching style.',
    ],
    close: 'Less improvising. More confident, intentional guidance.',
  },
  {
    n: 4,
    title: '12-WEEK YIN YOGA CLASS CALENDAR™',
    hook: 'Stop deciding what to teach one class at a time.',
    lead:
      'Access a ready-organized 12-week planning framework designed to help you create variety across your teaching schedule. Each stage provides a clear direction for:',
    bullets: [
      'class focus',
      'sequence selection',
      'variation',
      'teaching themes',
      'different class formats',
      'practical rotation',
    ],
    notes: [
      'Use the calendar exactly as structured or adapt it to your own weekly schedule.',
      "You'll also see how to modify the framework when you teach more or fewer classes.",
    ],
    close: 'Open the calendar and know where your next classes are going.',
  },
  {
    n: 5,
    title: 'YIN YOGA INSTRUCTOR PRINTABLE TOOLKIT™',
    hook:
      'Keep your teaching process organized without creating another administration system from scratch.',
    lead: 'Access practical reusable instructor resources including:',
    bullets: [
      'planning sheets',
      'class-preparation checklists',
      'prop checklists',
      'class records',
      'sequence trackers',
      'theme planners',
      'adaptation notes',
      'quick-reference pages',
      'class indexes',
      'post-class reflection tools',
    ],
    notes: [
      "Use them to record what you've taught, organize what you want to teach next and keep your strongest classes easy to find.",
    ],
    close: 'Less scattered planning. One organized teaching workflow.',
  },
]

export const benefits: Benefit[] = [
  {
    icon: 'classes',
    title: 'Teach Ready-Made Classes',
    description: 'Choose complete sessions instead of rebuilding every class.',
  },
  {
    icon: 'poses',
    title: 'Access Organized Pose Options',
    description:
      'Find variations, props and alternatives without searching across multiple resources.',
  },
  {
    icon: 'target',
    title: 'Choose the Right Sequence Faster',
    description:
      'Use targeted practices organized around different teaching focuses.',
  },
  {
    icon: 'voice',
    title: 'Guide the Class With More Structure',
    description:
      'Access prepared cueing, transitions and teaching language.',
  },
  {
    icon: 'plan',
    title: 'Plan Without a Blank Page',
    description:
      'Use templates and class-building frameworks instead of creating every session from zero.',
  },
  {
    icon: 'time',
    title: 'Spend Less Time Preparing',
    description:
      'Keep your classes, sequences and teaching tools together in one professional system.',
  },
]

export const objectionSection = {
  heading:
    'YOU DON’T NEED MORE YOGA CONTENT SCATTERED ACROSS THE INTERNET',
  intro: 'You may be thinking:',
  thoughts: [
    '“I already know how to teach Yin Yoga.”',
    '“I’ve already completed my training.”',
    '“I already know plenty of poses.”',
    '“I can find sequences online.”',
    '“I already have classes that work.”',
  ],
  pivot: 'Exactly.',
  body: [
    'YIN YOGA READY™ isn’t designed to teach experienced instructors what Yin Yoga is.',
    'It’s designed to make the planning and preparation side of teaching easier to organize.',
  ],
  steps: [
    'Choose the class.',
    'Check the sequence.',
    'Review the poses.',
    'Prepare the props.',
    'Check the modifications.',
    'Use the teaching cues.',
    'Adapt when needed.',
    'Teach.',
  ],
  close: [
    "Then open the system again when it's time to prepare your next session.",
    'You bring the teaching experience. We give you the ready-to-use structure.',
  ],
}

export const beforeAfter = {
  heading: 'FROM “WHAT AM I TEACHING NEXT?” TO A COMPLETE YIN YOGA SYSTEM',
  intro: 'Think about the difference.',
  today: [
    'Sequences saved across different folders.',
    'Random websites bookmarked.',
    'Instagram posts saved for later.',
    "Classes repeated because they're already prepared.",
    'Transitions created at the last minute.',
    'Modifications remembered on the spot.',
    'New sessions constantly being rebuilt.',
    'Teaching notes scattered everywhere.',
  ],
  tomorrow: [
    'Your pose library.',
    'Your ready-to-teach classes.',
    'Your targeted sequences.',
    'Your class builder.',
    'Your cueing and transitions.',
    'Your teaching toolkit.',
    'Your class themes.',
    'Your prop and modification guide.',
    'Your teaching-language vault.',
    'Your 12-week calendar.',
    'Your instructor tools.',
  ],
  close: [
    'One organized professional Yin Yoga resource.',
    "That's what YIN YOGA READY™ is designed to give you.",
  ],
}

export const offerIncludes = [
  '6 Complete Professional Modules',
  '5 Premium Bonuses',
  'Ready-to-Teach Yin Yoga Classes',
  'Pose & Variation Library',
  'Targeted Sequence Vault',
  'Class Builder System',
  'Cueing & Teaching Scripts',
  'Props & Modification Resources',
  'Instructor Planning Tools',
]

export const guaranteeSteps = [
  'Go through the six modules.',
  'Open the ready-to-teach classes.',
  'Explore the pose and sequence resources.',
  'Check the class-building system.',
  'Review the cueing and modification tools.',
  'Open the five premium bonuses.',
  'See how the material fits into the way you teach Yin Yoga.',
]

export const testimonials: Testimonial[] = [
  {
    text: "I've been teaching Yin for nine years and my planning was still a mess of screenshots and half-finished notes. Now I open the Ready-to-Teach Classes module, pick the 60-minute hip sequence, glance at the prop suggestions and I'm done. I got my Sunday afternoons back, and honestly my classes are more coherent than when I was building them from scratch.",
    author: 'Charlotte Hughes',
    role: 'Yin Yoga Instructor',
    location: 'Bristol, United Kingdom',
  },
  {
    text: 'The Cueing & Teaching Scripts module is what sold me. I always struggled with what to say during a four-minute hold — I either overtalked or went completely silent. Having prepared language for settling, breathing and quiet moments changed how my students experience the class. Three of them told me the practice feels "deeper" now.',
    author: 'Danielle Prescott',
    role: 'Studio Owner & Yin Teacher',
    location: 'Vancouver, Canada',
  },
  {
    text: "I teach six Yin classes a week across two studios and repetition was becoming a real problem. The 12-Week Class Calendar and the Sequence Vault mean every week has a different focus without me inventing anything. I used to spend four or five hours planning. Now it's under thirty minutes.",
    author: 'Rebecca Alvarez',
    role: 'RYT-500, Yin Yoga Instructor',
    location: 'Austin, Texas, United States',
  },
  {
    text: 'My groups are very mixed — a few students in their twenties, several over sixty, two with hip replacements. The Props & Modifications Quick Guide is now printed and sitting in my bag. I can adapt a pose in seconds without derailing the structure of the class for everyone else.',
    author: 'Fiona Mackenzie',
    role: 'Yoga Teacher (Yin & Restorative)',
    location: 'Melbourne, Australia',
  },
  {
    text: "I completed my Yin training two years ago and still felt like I was improvising every session. This isn't a course that re-teaches you Yin — it's the structure nobody gave me in training. The Class Builder blocks taught me how experienced teachers actually assemble a session. I walk in prepared now.",
    author: 'Sophie Bennett',
    role: 'Yin Yoga Instructor',
    location: 'Manchester, United Kingdom',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Is this a physical product?',
    a: 'No. YIN YOGA READY™ is a 100% digital professional teaching resource.',
  },
  {
    q: 'Who is this designed for?',
    a: 'It is designed for Yin Yoga instructors, Yoga teachers who include Yin Yoga in their classes and professionals who already understand the practice and want more structured, ready-to-use teaching resources.',
  },
  {
    q: 'Is this a beginner Yin Yoga certification or teacher training?',
    a: 'No. YIN YOGA READY™ is not designed to replace professional Yoga education or certification. It is a practical teaching and planning resource for instructors.',
  },
  {
    q: 'Is this another generic collection of Yin Yoga sequences?',
    a: 'No. The focus is implementation. The system combines complete classes, poses, variations, targeted sequences, modifications, teaching language, class-building frameworks and planning resources.',
  },
  {
    q: 'Do I need to create all the classes myself?',
    a: 'No. The purpose of the system is to give you ready-to-teach classes and prepared structures you can use or adapt instead of beginning every session with a blank page.',
  },
  {
    q: 'Does it only include complete classes?',
    a: "No. You'll also receive a pose and variation library, targeted sequences, class-building tools, cueing, transitions, modifications, planning resources and instructor tools.",
  },
  {
    q: 'Can I adapt the classes?',
    a: 'Yes. The system is designed to support adaptation according to your teaching context, class length, available props, student experience and your professional judgment.',
  },
  {
    q: 'Are different class durations included?',
    a: "Yes. The system includes structures designed around different teaching durations so you don't have to rebuild an entire session every time your schedule changes.",
  },
  {
    q: 'Does the system include modifications and props?',
    a: 'Yes. Practical variations, prop options and modification resources are included throughout the system, with an additional dedicated Props & Modifications Quick Guide™.',
  },
  {
    q: 'Will this teach me how to become a Yoga instructor?',
    a: 'No. This is not an instructor certification program. It is designed for instructors who want practical material to support class preparation and delivery.',
  },
  {
    q: 'Does this provide medical treatment or rehabilitation guidance?',
    a: 'No. YIN YOGA READY™ is an educational and professional teaching resource. It does not provide medical advice, diagnosis, treatment, rehabilitation or individualized healthcare guidance.',
  },
  {
    q: 'Can this help reduce my class-planning time?',
    a: "That's exactly what the system is designed to support: giving you organized, ready-to-use teaching material so you don't have to continuously search for and build every class from zero.",
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive digital access immediately after purchase.",
  },
]

export const finalSection = {
  heading: 'ONE LAST QUESTION',
  intro:
    'How many times have you finished one class and immediately thought:',
  quote: '“What am I going to teach next?”',
  keeps: [
    'Another week can pass.',
    'You can keep searching online.',
    'Keep saving random sequences.',
    'Keep rebuilding classes.',
    'Keep deciding which poses fit together.',
    'Keep writing new cueing.',
    'Keep recreating modifications.',
    "Keep repeating the same sessions because they're already prepared.",
    'And keep facing the same planning process next week.',
  ],
  or: 'Or you can open one organized system.',
  steps: [
    'Choose the class.',
    'Check the sequence.',
    'Prepare the props.',
    'Review the modifications.',
    'Use the teaching structure.',
    'Adapt it to your students.',
    'And teach.',
  ],
}

export const purchases: Purchase[] = [
  { name: 'Emma W.', city: 'Brighton', country: 'United Kingdom' },
  { name: 'Sarah J.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Olivia M.', city: 'Toronto', country: 'Canada' },
  { name: 'Claire D.', city: 'Calgary', country: 'Canada' },
  { name: 'Jessica T.', city: 'Portland, OR', country: 'United States' },
  { name: 'Rachel B.', city: 'Denver, CO', country: 'United States' },
  { name: 'Megan S.', city: 'Asheville, NC', country: 'United States' },
  { name: 'Amelia K.', city: 'Sydney', country: 'Australia' },
  { name: 'Hannah P.', city: 'Perth', country: 'Australia' },
  { name: 'Sophie L.', city: 'Edinburgh', country: 'United Kingdom' },
]
